import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * EAT-3 — the Promotion discount report: promotion discount by store,
 * business day and item, from the sale lines tills already upload. Amounts are
 * integer minor units as strings; quantities are decimal text.
 */

type Amounts = {
  grossMinor: string;
  promotionDiscountMinor: string;
  otherDiscountMinor: string;
  paymentDiscountMinor: string;
  unsplitDiscountMinor: string;
  returnedMinor: string;
  netMinor: string;
};

export type PromotionDiscountRow = Amounts & {
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  day: string;
  currency: string | null;
  sku: string | null;
  name: string;
  unit: string;
  quantitySold: string;
  quantityReturned: string;
  saleLines: number;
  promotedLines: number;
  refundLines: number;
};

export type PromotionDiscountDayRow = Amounts & {
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  day: string;
  currency: string | null;
  items: number;
  saleLines: number;
  promotedLines: number;
  refundLines: number;
};

export type PromotionDiscountTotal = Amounts & {
  currency: string | null;
  saleLines: number;
  promotedLines: number;
  refundLines: number;
};

export type PromotionDiscountReport = {
  fromDate: string;
  toDate: string;
  from: string;
  to: string;
  timeZone: string;
  timeZoneAmbiguous: boolean;
  totals: PromotionDiscountTotal[];
  days: PromotionDiscountDayRow[];
  rows: PromotionDiscountRow[];
};

export type PromotionDiscountReportFilters = {
  locations: { id: string; code: string; name: string }[];
};

/** Business days `fromDate..toDate`, both included, read in the tenant's zone by the server. */
export type PromotionDiscountReportScope = {
  fromDate: string;
  toDate: string;
  locationId?: string;
  onlyPromoted?: boolean;
};

/** The longest range the server accepts. */
export const PROMOTION_DISCOUNT_REPORT_MAX_DAYS = 31;

function scopeParams(scope: PromotionDiscountReportScope): Record<string, string> {
  const params: Record<string, string> = { fromDate: scope.fromDate, toDate: scope.toDate };
  if (scope.locationId) params.locationId = scope.locationId;
  if (scope.onlyPromoted) params.onlyPromoted = 'true';
  return params;
}

export function usePromotionDiscountReport(scope: PromotionDiscountReportScope | null) {
  return useQuery({
    queryKey: ['reports', 'promotion-discounts', scope] as const,
    enabled: scope !== null,
    queryFn: async (): Promise<PromotionDiscountReport> =>
      (
        await apiClient.get<PromotionDiscountReport>('/reports/promotion-discounts', {
          params: scopeParams(scope as PromotionDiscountReportScope),
        })
      ).data,
  });
}

export function usePromotionDiscountReportFilters() {
  return useQuery({
    queryKey: ['reports', 'promotion-discounts', 'filters'] as const,
    queryFn: async (): Promise<PromotionDiscountReportFilters> =>
      (await apiClient.get<PromotionDiscountReportFilters>('/reports/promotion-discounts/filters'))
        .data,
    staleTime: 60_000,
  });
}

/** Download the item or the per-store-day CSV under the given name. */
export async function downloadPromotionDiscountCsv(
  scope: PromotionDiscountReportScope,
  view: 'items' | 'days',
  fileName: string,
): Promise<void> {
  const res = await apiClient.get<Blob>('/reports/promotion-discounts/export', {
    params: { ...scopeParams(scope), view },
    responseType: 'blob',
    timeout: 120_000,
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
